import React, { useEffect, useState } from "react";
import {
  Box, Typography, Chip, Button,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import TuneIcon from "@mui/icons-material/Tune";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import InventoryIcon from "@mui/icons-material/Inventory";
import { useNotifier } from "../../../Core/Notifier";
import StockAdjustmentDialog from "./StockAdjustmentDialog";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

const REASONS = [
  { value: "count_correction", label: "Count Correction" },
  { value: "damaged", label: "Damaged Goods" },
  { value: "expired", label: "Expired" },
  { value: "theft", label: "Theft / Loss" },
  { value: "returned", label: "Returned Stock" },
  { value: "other", label: "Other" },
];

const StockAdjustments: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const [adjustments, setAdjustments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<any[]>([]);

  // Dialog state
  const [open, setOpen] = useState(false);

  const getHeaders = () => {
    const t = localStorage.getItem("token") || "";
    return { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` };
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [adjRes, prodRes] = await Promise.all([
        fetch(`${API_URL}/warehouse/adjustments?limit=100`, { headers: getHeaders() }),
        fetch(`${API_URL}/warehouse/dashboard`, { method: "POST", headers: getHeaders(), body: JSON.stringify({ track_only: true }) }),
      ]);
      const [adjJson, prodJson] = await Promise.all([adjRes.json(), prodRes.json()]);
      if (adjJson.success) setAdjustments(adjJson.data || []);
      if (prodJson.success) setProducts(prodJson.data || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const openDialog = () => setOpen(true);

  return (
    <Box sx={{ bgcolor: "#FAFAFA", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #E5E7EB", px: 3, py: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: 3, bgcolor: "#FFFBEB", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <TuneIcon sx={{ fontSize: 22, color: "#D97706" }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography fontSize={24} fontWeight={700} color="#111827" letterSpacing="-0.03em">Stock Adjustments</Typography>
            <Typography fontSize={13} color="#9CA3AF">Manual stock corrections with full audit trail</Typography>
          </Box>
          <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={openDialog}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: "#00838f", px: 3, "&:hover": { bgcolor: "#006064" } }}>
            New Adjustment
          </Button>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        {/* Adjustments History */}
        <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #E5E7EB", overflow: "hidden" }}>
          <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid #F3F4F6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography fontSize={14} fontWeight={600} color="#111827">Adjustment History</Typography>
            <Typography fontSize={12} color="#9CA3AF">{adjustments.length} records</Typography>
          </Box>
          {loading ? (
            <Box sx={{ textAlign: "center", py: 8 }}><CircularProgress size={28} sx={{ color: "#00838f" }} /></Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#F9FAFB" }}>
                    {["Date", "Product", "Type", "Qty Change", "Before", "After", "Reason", "Notes"].map(h => (
                      <TableCell key={h} sx={{ fontSize: 10, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.05em", py: 1.5 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {adjustments.map((a: any) => {
                    const isAdd = a.qty > 0;
                    return (
                      <TableRow key={a.id} sx={{ "&:hover": { bgcolor: "#F9FAFB" } }}>
                        <TableCell sx={{ fontSize: 12, color: "#374151", whiteSpace: "nowrap" }}>
                          {new Date(a.created_at).toLocaleDateString()}{" "}
                          <Typography component="span" fontSize={10} color="#9CA3AF">{new Date(a.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={13} fontWeight={600} color="#111827"
                            sx={{ cursor: "pointer", "&:hover": { color: "#00838f" } }}
                            onClick={() => a.product_id && navigate(`/product-history/${a.product_id}`)}>
                            {a.product?.productName || `#${a.product_id}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={isAdd ? "Add" : "Deduct"} size="small"
                            sx={{ height: 22, fontSize: 10, fontWeight: 600, bgcolor: isAdd ? "#F0FDF4" : "#FEF2F2", color: isAdd ? "#059669" : "#DC2626" }} />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {isAdd ? <ArrowUpwardIcon sx={{ fontSize: 14, color: "#059669" }} /> : <ArrowDownwardIcon sx={{ fontSize: 14, color: "#DC2626" }} />}
                            <Typography fontSize={13} fontWeight={700} color={isAdd ? "#059669" : "#DC2626"}>
                              {isAdd ? "+" : ""}{Number(a.qty).toLocaleString()}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, color: "#6B7280", fontFamily: "monospace" }}>{Number(a.stock_before).toLocaleString()}</TableCell>
                        <TableCell sx={{ fontSize: 12, color: "#374151", fontWeight: 600, fontFamily: "monospace" }}>{Number(a.stock_after).toLocaleString()}</TableCell>
                        <TableCell>
                          {a.reason ? (
                            <Chip label={REASONS.find(r => r.value === a.reason)?.label || a.reason} size="small" variant="outlined"
                              sx={{ height: 20, fontSize: 10, fontWeight: 500, borderColor: "#E5E7EB", color: "#6B7280" }} />
                          ) : <Typography fontSize={11} color="#D1D5DB">—</Typography>}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11, color: "#9CA3AF", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.notes || "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                  {adjustments.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: "center", py: 8 }}>
                        <InventoryIcon sx={{ fontSize: 40, color: "#E5E7EB", mb: 1 }} />
                        <Typography fontSize={14} color="#9CA3AF">No adjustments recorded yet</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>

      {/* New Adjustment Dialog (shared) */}
      <StockAdjustmentDialog
        open={open}
        onClose={() => setOpen(false)}
        products={products}
        onSuccess={loadData}
      />
    </Box>
  );
};

export default StockAdjustments;
